import PropTypes from 'prop-types'
import { formatDate } from '../../../../common/utils'
import Hyperlink from '../../HyperLink'
import Img from '../../Img'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import NextLink from 'next/link'

const PostItem = ({
  data: {
    title,
    shortDescription,
    description,
    metaDescription,
    author,
    externalLink,
    path,
    thumbnailAsset,
    category,
    updatedAt,
    publishedAt,
  },
  categoryTitle,
}) => {
  const isContainStyle = useMemo(
    () =>
      category.type === 'newsroom' &&
      (category.slug === 'in-the-news' || category.slug === 'author'),
    [category.slug, category.type]
  )

  return (
    <div
      className="mb-8 mt-2 grid grid-cols-12 gap-2 md:gap-6"
      data-testid="posts-item"
    >
      {thumbnailAsset ? (
        <div
          className={classNames(
            'lg:col-span-4',
            {
              'col-span-full h-36 bg-gray-130 md:col-span-4':
                category.type === 'newsroom',
            },
            { 'h-32 w-full lg:h-24': isContainStyle },
            { 'col-span-4 sm:col-span-3': category.type !== 'newsroom' }
          )}
          data-testid="thumbnail"
        >
          {!isContainStyle ? (
            <NextLink
              rel={externalLink ? 'nofollow' : undefined}
              href={`${path}`}
              data-testid="imgLink"
            >
              <Img
                src={thumbnailAsset.url}
                objectFit="contain"
                alt={thumbnailAsset.title}
                className="m-auto h-full w-38"
              />
            </NextLink>
          ) : (
            <NextLink
              rel={externalLink ? 'nofollow' : undefined}
              href={`${path}`}
              data-testid="imgLink"
            >
              <Img
                objectFit="cover"
                src={thumbnailAsset.url}
                alt={thumbnailAsset.title}
                className="m-auto h-full w-full"
              />
            </NextLink>
          )}
        </div>
      ) : null}
      <div
        className={classNames(
          'text-left',
          {
            'col-span-8 sm:col-span-9 lg:col-span-8':
              thumbnailAsset && category.type !== 'newsroom',
          },
          { 'col-span-12': !thumbnailAsset },
          {
            'col-span-full md:col-start-5 lg:col-span-8':
              category.type === 'newsroom',
          }
        )}
      >
        <NextLink
          href={path}
          className="break-words text-2xl leading-7 text-blue-700"
          rel={externalLink ? 'nofollow' : undefined}
        >
          {title}
        </NextLink>
        <div className="my-3 flex text-xs text-gray-510 md:my-1">
          {author?.fullName && (
            <NextLink
              href={`/author/${author.slug}/`}
              className="mr-2 font-bold text-gray-510 lg:p-0"
            >
              {author.fullName}
            </NextLink>
          )}
          <p className={author?.fullName ? '' : 'font-medium'}>
            {formatDate(
              new Date(publishedAt ? publishedAt : updatedAt).toUTCString()
            )}
          </p>
        </div>
        {(shortDescription || metaDescription || description) && (
          <p
            data-testid="short-description"
            className="pb-2 text-sm text-gray-510 md:pb-1"
          >
            {shortDescription ||
              (description
                ? `${description.substr(0, 100)}...`
                : metaDescription)}
          </p>
        )}
        <Hyperlink
          href={`${path}`}
          childrenClassNames="text-s hover:text-blue-700"
          rel={externalLink ? 'nofollow' : undefined}
          title={categoryTitle}
          clickTracking={true}
        >
          Read&nbsp;more
        </Hyperlink>
      </div>
    </div>
  )
}

PostItem.defaultProps = {
  data: {},
  categoryTitle: '',
}

PostItem.propTypes = {
  data: PropTypes.object,
  categoryTitle: PropTypes.string,
}

export default PostItem
